import { createFileRoute, Link } from "@tanstack/react-router";
import React, { useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  Award,
  Briefcase,
  Building2,
  CalendarDays,
  CheckCircle2,
  Clock,
  HelpCircle,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import {
  useStudentPlacement,
  usePlacementOpportunities,
  useUpdateOrientation,
  useMyPlacementRequests,
  useApplyToSlot,
  useWithdrawPlacementRequest,
  type StudentPlacementRequest,
} from "@/hooks/useStudentData";
import { useCompleteMyPlacement, usePlacementCompletion } from "@/hooks/useCv";
import { PageSkeleton } from "@/components/shared/Skeletons";
import { useTierAccess } from "@/hooks/useTierAccess";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface RoadmapItem {
  step: string;
  title: string;
  desc: string;
  status: "completed" | "in_progress" | "locked";
}

interface PlacementData {
  id?: string;
  partner_name?: string;
  slot_title?: string;
  location?: string;
  schedule?: string;
  daily_hours?: string;
  start_date?: string;
  progress?: number;
  hours_logged?: number;
  hours_required?: number;
  supervisor_name?: string;
  supervisor_role?: string;
  supervisor_email?: string;
  supervisor_phone?: string;
  roadmap?: RoadmapItem[];
  orientation?: Record<string, boolean>;
}

interface PlacementSlot {
  id: string | number;
  title?: string;
  company?: string;
  location?: string;
  requirements?: string | string[];
  skills?: string | string[];
  pac_notes?: string;
}

export const Route = createFileRoute("/student/placement/")({ component: StudentPlacement });

/** Mirrors FreeTierRequestLimit in the API so the cap is explained before the
 *  server refuses the fourth application. The server remains the authority. */
const FREE_APPLICATION_LIMIT = 3;

/**
 * CompletionPanel is where a placement turns into a certificate.
 *
 * The approved hours come from the server rather than the local progress figure,
 * because only partner-approved work logs count toward the requirement. Claiming
 * is idempotent, so a second click cannot mint a duplicate certificate.
 */
function CompletionPanel({ placementId }: { placementId?: string }) {
  const numericId = placementId ? Number(placementId) : undefined;
  const { data: completion, isLoading } = usePlacementCompletion(numericId);
  const complete = useCompleteMyPlacement();

  if (!numericId || Number.isNaN(numericId)) return null;

  const required = completion?.hours_required ?? 0;
  const approved = completion?.hours_approved ?? 0;
  const remaining = completion?.hours_remaining ?? Math.max(required - approved, 0);
  const issued = !!completion?.certificate_issued;
  const percent = required > 0 ? Math.min(Math.round((approved / required) * 100), 100) : 0;

  return (
    <div className="mt-6 border-t border-border pt-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-foreground">
          {issued ? "Completion certificate" : "Certificate eligibility"}
        </h3>
        {!isLoading && !issued && (
          <span className="text-caption text-muted-foreground tabular-nums">
            {approved} / {required} approved hours
          </span>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : issued ? (
        <Alert variant="success">
          <Award />
          <div className="flex flex-1 flex-wrap items-start justify-between gap-3">
            <AlertDescription className="flex-1 text-foreground">
              Your placement certificate is issued
              {completion?.cert_number ? ` (${completion.cert_number})` : ""}. It now counts as
              verified experience on your CV.
            </AlertDescription>
            <Button size="sm" variant="outline" asChild>
              <Link to="/student/certificates">
                View
                <ArrowRight />
              </Link>
            </Button>
          </div>
        </Alert>
      ) : completion?.eligible ? (
        <Alert variant="success" className="mb-4">
          <Award />
          <AlertDescription className="text-foreground">
            You have met the {required} approved hour requirement. Claim your certificate to lock
            in the placement as verified experience.
          </AlertDescription>
        </Alert>
      ) : (
        <Alert variant="info" className="mb-4">
          <HelpCircle />
          <AlertDescription className="text-foreground">
            {remaining} more approved {remaining === 1 ? "hour" : "hours"} needed. Only hours a
            partner has approved in a work log count toward the certificate.
          </AlertDescription>
        </Alert>
      )}

      {!issued && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Progress value={percent} className="flex-1" aria-label="Approved hours progress" />
          <Button
            size="sm"
            disabled={!completion?.eligible || complete.isPending}
            onClick={() =>
              complete.mutate(numericId, {
                onSuccess: () => toast.success("Placement certificate issued"),
                onError: (error: any) =>
                  toast.error(
                    error?.response?.data?.message || "Could not complete this placement yet",
                  ),
              })
            }
          >
            {complete.isPending ? "Claiming..." : "Claim certificate"}
          </Button>
        </div>
      )}
    </div>
  );
}

function StudentPlacement() {
  const { data: placement, isLoading } = useStudentPlacement() as { data: PlacementData | undefined; isLoading: boolean };
  const { data: opportunities } = usePlacementOpportunities() as { data: PlacementSlot[] | undefined };
  const updateOrientation = useUpdateOrientation();
  const { isVerified } = useTierAccess();
  const { data: myRequests = [] } = useMyPlacementRequests();
  const apply = useApplyToSlot();
  const withdraw = useWithdrawPlacementRequest();
  const [applyError, setApplyError] = useState("");

  const liveRequests = myRequests.filter(
    (r) => r.status === "submitted" || r.status === "accepted",
  );
  const requestFor = (slotId: string | number) =>
    myRequests.find(
      (r) =>
        String(r.slot_id) === String(slotId) &&
        (r.status === "submitted" || r.status === "accepted"),
    );
  const atFreeLimit = !isVerified && liveRequests.length >= FREE_APPLICATION_LIMIT;

  if (isLoading) {
    return <PageSkeleton />;
  }

  const hasPlacement = !!placement?.id;
  const displayPlacement = placement || {
    partner_name: "Awaiting Placement",
    hours_required: 400,
    status: "pending",
  };

  const roadmap: RoadmapItem[] =
    placement?.roadmap ||
    ([
      {
        step: "01",
        title: "Profile Registration",
        desc: "Complete your basic identity and academic background.",
        status: "completed" as const,
      },
      {
        step: "02",
        title: "Work Readiness",
        desc: "Finish mandatory orientation modules and assessments.",
        status: "in_progress" as const,
      },
      {
        step: "03",
        title: "Institutional Verification",
        desc: "Submit your Dean's letter and ID for platform verification.",
        status: "locked" as const,
      },
      {
        step: "04",
        title: "Live Slot Matching",
        desc: "Access the marketplace and start your professional journey.",
        status: "locked" as const,
      },
    ] as RoadmapItem[]);

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Experience access"
        subtitle="Manage your professional roadmap, track placement hours, and explore verified career slots."
        breadcrumbs={[{ label: "Dashboard", path: "/student/dashboard" }, { label: "Placement" }]}
        action={
          <span>
            <Badge variant={hasPlacement ? "success" : "muted"} dot>
              {hasPlacement ? "Active Placement" : "In Queue"}
            </Badge>
          </span>
        }
      />

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
        <StatCard label="Progress" value={`${placement?.progress || 0}%`} icon={<TrendingUp />} />
        <StatCard label="Hours logged" value={placement?.hours_logged || 0} icon={<Clock />} />
        <StatCard
          label="Remaining"
          value={(placement?.hours_required || 0) - (placement?.hours_logged || 0)}
          icon={<CalendarDays />}
        />
        <StatCard
          label="Partner status"
          value={hasPlacement ? "Assigned" : "Pending"}
          icon={<Building2 />}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Left column */}
        <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
          {/* Live placement */}
          <section className="rounded-md border border-border bg-card p-4 md:p-6">
            <div className="mb-6 flex items-center gap-4">
              <div className="flex size-14 shrink-0 items-center justify-center rounded-md border border-border bg-background text-body">
                <Building2 className="size-6" aria-hidden />
              </div>
              <div className="min-w-0">
                <h2 className="text-title text-foreground">{displayPlacement.partner_name}</h2>
                <p className="text-sm text-muted-foreground">
                  {placement?.slot_title || "Professional Internship Slot"} •{" "}
                  {placement?.location || "Remote/On-site"}
                </p>
              </div>
            </div>

            {!hasPlacement ? (
              <Alert variant="warning" className="mb-5">
                <HelpCircle />
                <AlertDescription className="text-foreground">
                  Complete your orientation and verify your student status to unlock direct employer
                  matching.
                </AlertDescription>
              </Alert>
            ) : (
              <dl className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
                <InfoBlock label="Role" value={placement.slot_title ?? ""} />
                <InfoBlock label="Schedule" value={placement.schedule || "Mon - Fri"} />
                <InfoBlock label="Daily hours" value={placement.daily_hours || "08:00 - 16:00"} />
                <InfoBlock
                  label="Start date"
                  value={
                    placement.start_date
                      ? new Date(placement.start_date).toLocaleDateString()
                      : "TBD"
                  }
                />
              </dl>
            )}

            <div>
              <div className="mb-2 flex justify-between text-sm">
                <span className="font-medium text-foreground">Completion progress</span>
                <span className="text-muted-foreground tabular-nums">
                  {placement?.progress || 0}%
                </span>
              </div>
              <Progress
                value={placement?.progress || 0}
                className="h-2"
                aria-label="Completion progress"
              />
            </div>

            <CompletionPanel placementId={placement?.id} />
          </section>

          {/* Roadmap */}
          <SectionCard title="Access roadmap" noPadding>
            <ol className="divide-y divide-border">
              {roadmap.map((item: RoadmapItem, idx: number) => (
                <li
                  key={idx}
                  className={cn(
                    "flex items-start gap-4 px-4 py-4 md:px-5",
                    item.status === "in_progress" && "bg-muted/40",
                  )}
                >
                  <span
                    className={cn(
                      "font-mono text-sm tabular-nums",
                      item.status === "completed" ? "text-success" : "text-muted-foreground",
                    )}
                  >
                    {item.step}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">{item.title}</p>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                  {item.status === "in_progress" && (
                    <Badge variant="info" dot className="shrink-0">
                      In Progress
                    </Badge>
                  )}
                  {item.status === "completed" && (
                    <CheckCircle2
                      className="size-4 shrink-0 text-success"
                      aria-label="Completed"
                    />
                  )}
                </li>
              ))}
            </ol>
          </SectionCard>
        </div>

        {/* Right column */}
        <div className="flex min-w-0 flex-col gap-4">
          {/* Supervisor */}
          <section className="rounded-md border border-border bg-card p-4 text-center md:p-6">
            <Avatar className="mx-auto mb-3 size-20 rounded-full">
              <AvatarImage
                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=200"
                alt=""
              />
              <AvatarFallback className="text-base">
                {(placement?.supervisor_name || "U").charAt(0)}
              </AvatarFallback>
            </Avatar>
            <h2 className="text-base font-medium text-foreground">
              {placement?.supervisor_name || "Unassigned"}
            </h2>
            <p className="mb-5 text-sm text-muted-foreground">
              {placement?.supervisor_role || "Designated Supervisor"}
            </p>

            <ul className="mb-5 space-y-2.5 text-left">
              <SidebarContact
                icon={<Mail />}
                text={placement?.supervisor_email || "Pending verification"}
              />
              <SidebarContact
                icon={<Phone />}
                text={placement?.supervisor_phone || "Pending verification"}
              />
              <SidebarContact icon={<MapPin />} text={placement?.location || "Waitlist"} />
            </ul>

            <Button variant="outline" className="w-full">
              <MessageSquare />
              Send message
            </Button>
          </section>

          {/* Orientation checklist */}
          <SectionCard title="Orientation checklist">
            <ul className="space-y-1">
              {[
                { id: "work_ethics", label: "Work ethics" },
                { id: "communication", label: "Communication" },
                { id: "timekeeping", label: "Timekeeping" },
                { id: "reporting", label: "Reporting" },
                { id: "professional_behavior", label: "Professional behavior" },
              ].map((module) => {
                const isDone = placement?.orientation?.[module.id];
                return (
                  <li key={module.id} className="flex min-h-9 items-center justify-between gap-3">
                    <span
                      className={cn(
                        "text-sm",
                        isDone ? "text-foreground" : "text-muted-foreground",
                      )}
                    >
                      {module.label}
                    </span>
                    {isDone ? (
                      <CheckCircle2 className="size-4 text-success" aria-label="Completed" />
                    ) : (
                      <button
                        type="button"
                        aria-label={`Mark ${module.label} as complete`}
                        onClick={() =>
                          updateOrientation.mutate({ module: module.id, status: true })
                        }
                        className="size-4 rounded-full border border-hairline-strong transition-colors hover:border-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </SectionCard>
        </div>
      </div>

      {/* Opportunities */}
      <section className="space-y-3">
        <h2 className="text-title text-foreground">Verified opportunity preview</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {opportunities?.length ? (
            opportunities.map((slot: PlacementSlot) => {
              const tags = ([] as string[])
                .concat(
                  Array.isArray(slot.requirements)
                    ? slot.requirements
                    : (slot.requirements || "")
                        .split(",")
                        .map((s: string) => s.trim())
                        .filter(Boolean),
                  Array.isArray(slot.skills)
                    ? slot.skills
                    : (slot.skills || "")
                        .split(",")
                        .map((s: string) => s.trim())
                        .filter(Boolean),
                )
                .slice(0, 3);
              const unlocked = !!requestFor(slot.id) || isVerified;
              return (
                <article
                  key={slot.id}
                  className="flex flex-col rounded-md border border-border bg-card p-4 transition-colors hover:border-hairline-strong md:p-5"
                >
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-base font-medium text-foreground">{slot.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        {slot.company || "Partner Company"} • {slot.location}
                      </p>
                    </div>
                    <Badge variant={unlocked ? "success" : "muted"} className="shrink-0">
                      {requestFor(slot.id) ? "Applied" : isVerified ? "Unlocked" : "Verify to Unlock"}
                    </Badge>
                  </div>
                  {tags.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-1.5">
                      {tags.map((s: string) => (
                        <Badge key={s} variant="outline">
                          {s}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <p className="border-t border-border pt-3 text-caption text-muted-foreground italic">
                    {slot.pac_notes || "Structured completion record and certificate eligible."}
                  </p>

                  <ApplySlotAction
                    slot={slot}
                    existing={requestFor(slot.id)}
                    atFreeLimit={atFreeLimit}
                    isPending={apply.isPending}
                    onApply={() =>
                      apply.mutate({ slot_id: Number(slot.id) }, {
                        onError: (e: any) =>
                          setApplyError(
                            e?.response?.data?.message ||
                              "Could not submit your application. Please try again.",
                          ),
                      })
                    }
                  />
                  {applyError && (
                    <Alert variant="destructive" className="mt-3">
                      <AlertCircle />
                      <AlertDescription className="text-foreground">{applyError}</AlertDescription>
                    </Alert>
                  )}
                </article>
              );
            })
          ) : (
            <div className="rounded-md border border-dashed border-border bg-card p-5 md:col-span-2">
              <p className="text-sm font-medium text-foreground">
                No verified opportunities available yet
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Published partner work slots from the API will appear here as soon as they are
                available for your account.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Applications */}
      <section className="space-y-3">
        <h2 className="text-title text-foreground">My applications</h2>

        {myRequests.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-card p-5">
            <p className="text-sm font-medium text-foreground">
              You have not applied to any opportunities yet
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {isVerified
                ? "Apply to any published slot above and track the partner's decision here."
                : `Verified students can apply to unlimited opportunities. On the free tier you can hold ${FREE_APPLICATION_LIMIT} applications at a time.`}
            </p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {myRequests.map((request: StudentPlacementRequest) => (
              <article
                key={request.id}
                className="rounded-md border border-border bg-card p-4 md:p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-base font-medium text-foreground">
                      {request.slot_title || `Opportunity #${request.slot_id}`}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {[request.company, request.location].filter(Boolean).join(" • ") || "—"}
                    </p>
                  </div>
                  <Badge
                    dot
                    className="shrink-0 capitalize"
                    variant={
                      request.status === "accepted"
                        ? "success"
                        : request.status === "submitted"
                          ? "warning"
                          : "muted"
                    }
                  >
                    {request.status === "submitted" ? "Awaiting review" : request.status}
                  </Badge>
                </div>

                {request.status === "submitted" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-4"
                    disabled={withdraw.isPending}
                    onClick={() =>
                      withdraw.mutate(request.id, {
                        onError: (e: any) =>
                          setApplyError(
                            e?.response?.data?.message ||
                              "Could not withdraw this application.",
                          ),
                      })
                    }
                  >
                    Withdraw application
                  </Button>
                )}
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * The action under a single opportunity. Which control appears depends on what
 * the student already holds for that slot, so the card shows the real state
 * rather than a decorative "Unlocked" label.
 */
function ApplySlotAction({
  existing,
  atFreeLimit,
  isPending,
  onApply,
}: {
  slot: PlacementSlot;
  existing?: StudentPlacementRequest;
  atFreeLimit: boolean;
  isPending: boolean;
  onApply: () => void;
}) {
  if (existing) {
    return (
      <Button variant="outline" disabled className="mt-4 w-full">
        <CheckCircle2 />
        {existing.status === "accepted" ? "Application accepted" : "Application submitted"}
      </Button>
    );
  }

  if (atFreeLimit) {
    return (
      <Alert variant="info" className="mt-4">
        <HelpCircle />
        <AlertDescription className="text-foreground">
          You have reached the free-tier limit of {FREE_APPLICATION_LIMIT} open applications.
          Verify your account to apply to more, or withdraw one above.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Button variant="outline" disabled={isPending} onClick={onApply} className="mt-4 w-full">
      <Briefcase />
      {isPending ? "Submitting..." : "Apply for this opportunity"}
    </Button>
  );
}

function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="eyebrow mb-1">{label}</dt>
      <dd className="truncate text-sm text-foreground">{value}</dd>
    </div>
  );
}

function SidebarContact({ icon, text }: { icon: React.ReactElement<any>; text: string }) {
  return (
    <li className="flex items-center gap-3">
      <span className="text-muted-foreground [&_svg]:size-4" aria-hidden>
        {icon}
      </span>
      <span className="min-w-0 truncate text-sm text-body-strong">{text}</span>
    </li>
  );
}

export default StudentPlacement;
